/**
 * 最小 ZIP 容器读写（M1-15 / DESIGN.md §6.4）。
 *
 * 为什么自己写而不引一个库：`.cvsm` 只用到 ZIP 的一个很窄的子集——
 * 单文件夹层级、两种压缩方法、无加密、无跨卷。而通用 ZIP 库要处理
 * ZIP64、加密、各种历史遗留字段，体积与攻击面都不划算。
 * 这里的实现刚好够用，且**读取路径全程带边界检查**（CLAUDE.md 规则 10）。
 *
 * 选 ZIP 而不是自定义容器，是为了 §6.4 说的那条好处：**可被外部工具解析**。
 * 用户能用系统自带的解压工具打开 .cvsm 看到里面的 manifest.json 与块文件，
 * 这在格式出问题时是救命的。
 */

/** ZIP 的压缩方法。只支持这两种。 */
export const ZipMethod = {
  /** 存储（不压缩）。已经压过的数据用这个，避免压两遍。 */
  Store: 0,
  /** Deflate。 */
  Deflate: 8,
} as const;

export type ZipMethodValue = (typeof ZipMethod)[keyof typeof ZipMethod];

export interface ZipEntry {
  readonly name: string;
  readonly data: Uint8Array;
  readonly method: ZipMethodValue;
  /** 未压缩时的字节数。 */
  readonly uncompressedSize: number;
  readonly crc32: number;
}

/** 读取时返回的条目，`data` 是**压缩后**的原始字节。 */
export interface RawZipEntry {
  readonly name: string;
  readonly method: ZipMethodValue;
  readonly compressed: Uint8Array;
  readonly uncompressedSize: number;
  readonly crc32: number;
}

export class ZipFormatError extends Error {
  constructor(message: string) {
    super(`.cvsm 容器损坏：${message}`);
    this.name = 'ZipFormatError';
  }
}

const LOCAL_HEADER_SIGNATURE = 0x04034b50;
const CENTRAL_HEADER_SIGNATURE = 0x02014b50;
const END_OF_CENTRAL_SIGNATURE = 0x06054b50;

/**
 * 单个条目的上限（512 MB）与条目数上限。
 *
 * 不设上限的话，一个畸形的 central directory 里写个 40 亿的条目数，
 * 读取方就会尝试分配一个天文数字大小的数组（§13.5）。
 */
const MAX_ENTRY_BYTES = 512 * 1024 * 1024;
const MAX_ENTRIES = 1_000_000;

/* ------------------------------------------------------------------ */
/* CRC32                                                               */
/* ------------------------------------------------------------------ */

const CRC_TABLE = (() => {
  const table = new Uint32Array(256);
  for (let n = 0; n < 256; n++) {
    let c = n;
    for (let k = 0; k < 8; k++) c = (c & 1) !== 0 ? 0xedb88320 ^ (c >>> 1) : c >>> 1;
    table[n] = c >>> 0;
  }
  return table;
})();

export function crc32(data: Uint8Array): number {
  let x = 0xffffffff;
  for (let i = 0; i < data.length; i++) {
    x = CRC_TABLE[(x ^ data[i]!) & 0xff]! ^ (x >>> 8);
  }
  return (x ^ 0xffffffff) >>> 0;
}

/* ------------------------------------------------------------------ */
/* 压缩                                                                 */
/* ------------------------------------------------------------------ */

/**
 * 用浏览器原生的 `CompressionStream` 做 deflate。
 *
 * §6.4 写的是「块自身用 zstd 预压缩」。zstd 需要额外的 wasm 模块（附录 A 有列），
 * 而 `deflate-raw` 是浏览器原生、零依赖、且 ZIP 规范原生支持的方法——
 * 外部解压工具能直接打开。先用它把格式与增量保存跑通；
 * 换 zstd 时只需改这两个函数与 manifest 里的 `compression` 字段。
 */
export async function deflateRaw(data: Uint8Array): Promise<Uint8Array> {
  const stream = new Blob([data as BlobPart]).stream().pipeThrough(new CompressionStream('deflate-raw'));
  return new Uint8Array(await new Response(stream).arrayBuffer());
}

export async function inflateRaw(data: Uint8Array, expectedSize: number): Promise<Uint8Array> {
  if (expectedSize > MAX_ENTRY_BYTES) {
    throw new ZipFormatError(`条目声明的解压后大小 ${expectedSize} 超过上限`);
  }
  const stream = new Blob([data as BlobPart])
    .stream()
    .pipeThrough(new DecompressionStream('deflate-raw'));
  const out = new Uint8Array(await new Response(stream).arrayBuffer());
  if (out.length !== expectedSize) {
    // 长度对不上说明数据被截断或篡改过。继续用它会把错误扩散到像素上
    throw new ZipFormatError(`解压后大小 ${out.length} 与声明的 ${expectedSize} 不符`);
  }
  return out;
}

/* ------------------------------------------------------------------ */
/* 写                                                                   */
/* ------------------------------------------------------------------ */

/** 构造一个条目（自动算 CRC，不做压缩——压缩由调用方决定）。 */
export function zipEntry(
  name: string,
  data: Uint8Array,
  options: { method: ZipMethodValue; uncompressedSize?: number; uncompressedCrc?: number },
): ZipEntry {
  return {
    name,
    data,
    method: options.method,
    uncompressedSize: options.uncompressedSize ?? data.length,
    crc32: options.uncompressedCrc ?? crc32(data),
  };
}

/**
 * 打包成 ZIP 字节。
 *
 * 布局：`[local header + data] × N` + `[central header] × N` + `end of central directory`。
 * 不写数据描述符（那是流式写入才需要的），因为这里所有大小都已知。
 */
export function writeZip(entries: readonly ZipEntry[]): Uint8Array {
  const encoder = new TextEncoder();
  const encodedNames = entries.map((entry) => encoder.encode(entry.name));

  let localSize = 0;
  for (const [i, entry] of entries.entries()) {
    localSize += 30 + encodedNames[i]!.length + entry.data.length;
  }
  let centralSize = 0;
  for (const name of encodedNames) centralSize += 46 + name.length;

  const out = new Uint8Array(localSize + centralSize + 22);
  const view = new DataView(out.buffer);
  const offsets: number[] = [];
  let pos = 0;

  for (const [i, entry] of entries.entries()) {
    const name = encodedNames[i]!;
    offsets.push(pos);
    view.setUint32(pos, LOCAL_HEADER_SIGNATURE, true);
    view.setUint16(pos + 4, 20, true); // 需要的版本
    view.setUint16(pos + 6, 0, true); // 标志位
    view.setUint16(pos + 8, entry.method, true);
    view.setUint16(pos + 10, 0, true); // 修改时间
    // 修改日期固定为 1980-01-01（ZIP 纪元起点）：**保存必须是确定性的**，
    // 写入当前时间会让同样的内容产出不同的字节，增量保存就没法比对了
    view.setUint16(pos + 12, 0x0021, true);
    view.setUint32(pos + 14, entry.crc32, true);
    view.setUint32(pos + 18, entry.data.length, true);
    view.setUint32(pos + 22, entry.uncompressedSize, true);
    view.setUint16(pos + 26, name.length, true);
    view.setUint16(pos + 28, 0, true); // extra 长度
    out.set(name, pos + 30);
    out.set(entry.data, pos + 30 + name.length);
    pos += 30 + name.length + entry.data.length;
  }

  const centralStart = pos;
  for (const [i, entry] of entries.entries()) {
    const name = encodedNames[i]!;
    view.setUint32(pos, CENTRAL_HEADER_SIGNATURE, true);
    view.setUint16(pos + 4, 20, true); // 创建版本
    view.setUint16(pos + 6, 20, true); // 需要的版本
    view.setUint16(pos + 8, 0, true);
    view.setUint16(pos + 10, entry.method, true);
    view.setUint16(pos + 12, 0, true);
    view.setUint16(pos + 14, 0x0021, true);
    view.setUint32(pos + 16, entry.crc32, true);
    view.setUint32(pos + 20, entry.data.length, true);
    view.setUint32(pos + 24, entry.uncompressedSize, true);
    view.setUint16(pos + 28, name.length, true);
    view.setUint16(pos + 30, 0, true); // extra
    view.setUint16(pos + 32, 0, true); // 注释
    view.setUint16(pos + 34, 0, true); // 磁盘号
    view.setUint16(pos + 36, 0, true); // 内部属性
    view.setUint32(pos + 38, 0, true); // 外部属性
    view.setUint32(pos + 42, offsets[i]!, true);
    out.set(name, pos + 46);
    pos += 46 + name.length;
  }

  view.setUint32(pos, END_OF_CENTRAL_SIGNATURE, true);
  view.setUint16(pos + 4, 0, true);
  view.setUint16(pos + 6, 0, true);
  view.setUint16(pos + 8, entries.length, true);
  view.setUint16(pos + 10, entries.length, true);
  view.setUint32(pos + 12, centralSize, true);
  view.setUint32(pos + 16, centralStart, true);
  view.setUint16(pos + 20, 0, true); // 注释长度

  return out;
}

/* ------------------------------------------------------------------ */
/* 读                                                                   */
/* ------------------------------------------------------------------ */

/**
 * 解析 ZIP，返回条目表（数据仍是压缩态）。
 *
 * 从 central directory 读而不是顺序扫 local header：前者是 ZIP 的权威索引，
 * 后者在有数据描述符或额外字段时会走偏。全程带边界检查——
 * 这是从磁盘/网络来的数据，任何字段都可能是恶意的。
 */
export function readZip(bytes: Uint8Array): RawZipEntry[] {
  if (bytes.length < 22) {
    throw new ZipFormatError('文件太小，装不下 ZIP 结尾记录');
  }
  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);

  // end of central directory 在文件末尾，但后面可能跟着注释，所以要往回找
  let eocd = -1;
  const searchFrom = Math.max(0, bytes.length - 22 - 0xffff);
  for (let i = bytes.length - 22; i >= searchFrom; i--) {
    if (view.getUint32(i, true) === END_OF_CENTRAL_SIGNATURE) {
      eocd = i;
      break;
    }
  }
  if (eocd < 0) throw new ZipFormatError('找不到 ZIP 结尾记录');

  const count = view.getUint16(eocd + 10, true);
  const centralStart = view.getUint32(eocd + 16, true);
  if (count > MAX_ENTRIES) throw new ZipFormatError(`条目数 ${count} 超过上限`);
  if (centralStart >= bytes.length) throw new ZipFormatError('central directory 偏移越界');

  const entries: RawZipEntry[] = [];
  const decoder = new TextDecoder();
  let pos = centralStart;

  for (let i = 0; i < count; i++) {
    if (pos + 46 > bytes.length) throw new ZipFormatError('central directory 被截断');
    if (view.getUint32(pos, true) !== CENTRAL_HEADER_SIGNATURE) {
      throw new ZipFormatError(`第 ${i} 个 central header 签名不对`);
    }
    const method = view.getUint16(pos + 10, true);
    const entryCrc = view.getUint32(pos + 16, true);
    const compressedSize = view.getUint32(pos + 20, true);
    const uncompressedSize = view.getUint32(pos + 24, true);
    const nameLength = view.getUint16(pos + 28, true);
    const extraLength = view.getUint16(pos + 30, true);
    const commentLength = view.getUint16(pos + 32, true);
    const localOffset = view.getUint32(pos + 42, true);

    if (compressedSize > MAX_ENTRY_BYTES || uncompressedSize > MAX_ENTRY_BYTES) {
      throw new ZipFormatError(`条目大小超过上限（压缩 ${compressedSize}、原始 ${uncompressedSize}）`);
    }
    if (pos + 46 + nameLength > bytes.length) throw new ZipFormatError('条目名越界');
    const name = decoder.decode(bytes.subarray(pos + 46, pos + 46 + nameLength));

    // 顺着 central directory 给的偏移去取数据，但要重新读 local header
    // 的名字与 extra 长度——两处的 extra 长度可以不同，这是常见的踩坑点
    if (localOffset + 30 > bytes.length) throw new ZipFormatError(`条目「${name}」的偏移越界`);
    if (view.getUint32(localOffset, true) !== LOCAL_HEADER_SIGNATURE) {
      throw new ZipFormatError(`条目「${name}」的 local header 签名不对`);
    }
    const localNameLength = view.getUint16(localOffset + 26, true);
    const localExtraLength = view.getUint16(localOffset + 28, true);
    const dataStart = localOffset + 30 + localNameLength + localExtraLength;
    if (dataStart + compressedSize > bytes.length) {
      throw new ZipFormatError(`条目「${name}」的数据被截断`);
    }

    if (method !== ZipMethod.Store && method !== ZipMethod.Deflate) {
      throw new ZipFormatError(`条目「${name}」用了不支持的压缩方法 ${method}`);
    }

    entries.push({
      name,
      method,
      compressed: bytes.subarray(dataStart, dataStart + compressedSize),
      uncompressedSize,
      crc32: entryCrc,
    });

    pos += 46 + nameLength + extraLength + commentLength;
  }

  return entries;
}

/** 取出条目的原始数据，按需解压并校验 CRC。 */
export async function readEntryData(entry: RawZipEntry): Promise<Uint8Array> {
  const data =
    entry.method === ZipMethod.Store
      ? entry.compressed.slice()
      : await inflateRaw(entry.compressed, entry.uncompressedSize);

  // CRC 校验不能省：磁盘位翻转与不完整写入都会在这里被拦下，
  // 否则错误会以"某几个像素莫名其妙变了"的形式出现
  const actual = crc32(data);
  if (actual !== entry.crc32) {
    throw new ZipFormatError(
      `条目「${entry.name}」CRC 校验失败（期望 ${entry.crc32.toString(16)}，实际 ${actual.toString(16)}）`,
    );
  }
  return data;
}
